import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";

import type { GetNoteHistoryResponse } from "@/core/types/(api)/cosignature";
import type { UUID } from "@/core/types/common";

import { adaptNoteHistory } from "../../helpers/adapters";
import { cosignatureKeys } from "../keyFactory";
import { getNoteHistory } from "../queryFunctions/getNoteHistory";
import type { NoteHistoryGroup } from "../../helpers/adapters";
import type { NoteHistoryViewAs } from "../../types";

type UseNoteHistoryProps<TData = NoteHistoryGroup[]> = {
  enabled?: boolean;
  noteId: UUID;
  select?: (data: GetNoteHistoryResponse) => TData;
  viewAs: NoteHistoryViewAs;
};

type NoteHistoryQueryOptions = UseQueryOptions<
  GetNoteHistoryResponse,
  unknown,
  GetNoteHistoryResponse,
  ReturnType<typeof cosignatureKeys.noteHistory>
>;

export function optionFactory(noteId: UUID, enabled = true) {
  return {
    enabled,
    queryFn: ({ signal }) => getNoteHistory(noteId, signal),
    queryKey: cosignatureKeys.noteHistory(noteId),
  } satisfies NoteHistoryQueryOptions;
}

export function useNoteHistory<TData = NoteHistoryGroup[]>({
  enabled,
  noteId,
  select,
  viewAs,
}: UseNoteHistoryProps<TData>) {
  const adaptForViewAs = useCallback(
    (data: GetNoteHistoryResponse) => adaptNoteHistory(data, viewAs) as TData,
    [viewAs],
  );

  return useQuery({ ...optionFactory(noteId, enabled), select: select ?? adaptForViewAs });
}
